import { CurrencyEstimate } from "@/components/currency-estimate";
import SeoMeta from "@/components/seo-meta";
import pricingSnapshot from "@/generated/pricing-snapshot.json";
import { vehiclePriceOf, vehicleDigits, type VehicleClass } from "@/lib/service-pricing";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEffect } from "react";
import { useSmartTranslation } from "@/hooks/useSmartTranslation";
import { useTranslation } from "react-i18next";
import { 
  Plane, 
  MapPin, 
  Shield, 
  Car, 
  CheckCircle, 
  Star,
  Users,
  ArrowRight,
  Phone,
  Camera
} from "lucide-react";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { breadcrumbSchema, trailFor } from "@/lib/breadcrumb-schema";
import PageBreadcrumbs from "@/components/page-breadcrumbs";

export default function LuxorAirportTransfers() {
  const SERVICE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Service",
    // schema.org value on the English prerender Google indexes
    "serviceType": "Airport Transfer", // i18n-exempt
    "provider": { "@type": "TravelAgency", "name": "AffordEgypt" },
    "areaServed": "Luxor",
    "offers": {
      "@type": "Offer",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "minPrice": pricingSnapshot.services["luxor-airport-transfer"].minPrice,
        "priceCurrency": pricingSnapshot.currency,
      },
      "availability": "https://schema.org/InStock",
    },
  };
  // The meta description quotes a price, so it is derived like every other
  // price on the page. A literal here is outside the drift check and is how
  // the visible prices came adrift in the first place.
  const sedanFrom = vehicleDigits("luxor-airport-transfer", "sedan") ?? "—";
  const { t } = useSmartTranslation();
  const { t: rawT } = useTranslation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Structured content is read with returnObjects, which useSmartTranslation
  // deliberately does not support -- it is a string-only helper. A locale that
  // has not been backfilled falls through to English rather than rendering an
  // empty section.
  const list = (key: string): any[] => {
    const value = rawT(key, { returnObjects: true });
    if (Array.isArray(value)) return value;
    const english = rawT(key, { returnObjects: true, lng: "en" });
    return Array.isArray(english) ? english : [];
  };

  // "From X EGP" comes from the build-time snapshot, never from a literal in
  // the locale file. See client/src/lib/service-pricing.ts.
  const fromPrice = (vehicle: VehicleClass) => {
    const digits = vehicleDigits("luxor-airport-transfer", vehicle);
    return digits === null ? "—" : rawT("airportTransfers.common.fromPrice", { price: digits });
  };

  const vehicleTypes = [
    {
      name: t("airportTransfers.common.sedan"),
      capacity: t("airportTransfers.common.passengers1to2"),
      price: fromPrice("sedan"),
      amountEGP: vehiclePriceOf("luxor-airport-transfer", "sedan"),
      features: list("airportTransfers.luxor.sedanFeatures"),
      icon: Car
    },
    {
      name: t("airportTransfers.common.minivan"),
      capacity: t("airportTransfers.common.passengers3to8"),
      price: fromPrice("minivan"),
      amountEGP: vehiclePriceOf("luxor-airport-transfer", "minivan"),
      features: list("airportTransfers.luxor.minivanFeatures"),
      icon: Users
    },
    {
      name: t("airportTransfers.common.van"),
      capacity: t("airportTransfers.common.passengers9to15"),
      price: fromPrice("van"),
      amountEGP: vehiclePriceOf("luxor-airport-transfer", "van"),
      features: list("airportTransfers.luxor.vanFeatures"),
      icon: Users
    }
  ];

  const banks = list("airportTransfers.luxor.banks");
  const pricingGuide = list("airportTransfers.luxor.pricingGuide");
  const cruiseItems = list("airportTransfers.luxor.cruiseItems");
  const calcItems = list("airportTransfers.luxor.calcItems");
  const includesItems = list("airportTransfers.luxor.includesItems");
  const changesItems = list("airportTransfers.luxor.changesItems");
  const steps = list("airportTransfers.luxor.steps");
  const beforeItems = list("airportTransfers.luxor.beforeItems");

  return (
    <>
      <SeoMeta
        title={t("airportTransfers.luxor.seoTitle")}
        description={t("airportTransfers.luxor.seoDescription", {
          interpolation: { price: `LE ${sedanFrom}` },
        })}
        canonical="https://affordegypt.com/luxor-airport-transfers"
        schema={[SERVICE_SCHEMA, breadcrumbSchema(trailFor("/luxor-airport-transfers")!)]}
      />

      <div className="min-h-screen bg-background">
        <Navbar />
        <PageBreadcrumbs />
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-primary/5 to-background py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Plane className="w-10 h-10 text-primary" />
                <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                  {t("airportTransfers.luxor.heroTitle")}
                </h1>
              </div>
              <p className="text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
                {t("airportTransfers.luxor.heroSubtitle")}
              </p>
              <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
                {t("airportTransfers.luxor.heroNote")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {/* Sentence-length CTA. Buttons are white-space:nowrap by
                    default, and the German label ("Erhalten Sie Ihr genaues
                    Transfer-Angebot") overflowed a 375px viewport and made the
                    page scroll sideways — the same failure already documented
                    on the Sinai guide. Let it wrap. */}
                <Button asChild size="lg" className="text-lg px-8 whitespace-normal max-w-full h-auto py-3">
                  <Link href="/transfers">
                    {t("airportTransfers.luxor.ctaQuote")}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>

                </Button>
                <a href="https://wa.me/201100765283" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" className="text-lg px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    {t("airportTransfers.common.whatsapp")}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Which bank. This leads because the riverbank, not the city name,
            is what decides the route and the fare. */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">{t("airportTransfers.luxor.banksTitle")}</h2>
              <p className="text-center text-muted-foreground mb-8 max-w-3xl mx-auto">
                {t("airportTransfers.luxor.banksIntro")}
              </p>

              <Card className="mb-8">
                <CardHeader>
                  <Plane className="w-10 h-10 text-primary mb-2" />
                  <CardTitle className="text-2xl">{t("airportTransfers.luxor.airportName")}</CardTitle>
                  <Badge variant="secondary" className="w-fit text-base font-semibold">
                    {t("airportTransfers.luxor.airportCode")}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>{t("airportTransfers.luxor.airportLocation")}</p>
                  <p>{t("airportTransfers.luxor.airportRouting")}</p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {banks.map((bank: any, index: number) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-primary" />
                        {bank.name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{bank.intro}</p>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-4">
                        {(bank.areas || []).map((area: string, idx: number) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                            <span className="text-sm">{area}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-muted-foreground">{bank.note}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-900">
                    <strong>{t("airportTransfers.luxor.bankWarningLabel")}</strong>{" "}
                    {t("airportTransfers.luxor.bankWarning")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* East vs West pricing guide */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">{t("airportTransfers.luxor.pricingGuideTitle")}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pricingGuide.map((row: any, index: number) => (
                  <div key={index} className="p-4 bg-muted/30 rounded-lg">
                    <h3 className="font-semibold mb-1">{row.label}</h3>
                    <p className="text-sm text-muted-foreground">{row.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Nile cruise moorings */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">{t("airportTransfers.luxor.cruiseTitle")}</h2>
              <p className="text-center text-muted-foreground mb-8">
                {t("airportTransfers.luxor.cruiseIntro")}
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {cruiseItems.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 p-3 bg-background rounded-lg">
                    <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted-foreground text-center">{t("airportTransfers.luxor.cruiseNote")}</p>
            </div>
          </div>
        </section>

        {/* How the price is calculated */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">{t("airportTransfers.luxor.calcTitle")}</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {calcItems.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Vehicle Options */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-4">{t("airportTransfers.luxor.vehiclesTitle")}</h2>
            <p className="text-center text-muted-foreground mb-12 max-w-3xl mx-auto">
              {t("airportTransfers.luxor.vehiclesIntro")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {vehicleTypes.map((vehicle, index) => (
                <Card key={index} className="relative hover:shadow-lg transition-shadow bg-background">
                  <CardHeader className="text-center">
                    <vehicle.icon className="w-16 h-16 text-primary mx-auto mb-4" />
                    <CardTitle className="text-2xl">{vehicle.name}</CardTitle>
                    <p className="text-muted-foreground">{vehicle.capacity}</p>
                    <Badge variant="secondary" className="text-lg font-semibold px-4 py-1 mt-2">
                      {vehicle.price}
                    </Badge>
                    <CurrencyEstimate amount={vehicle.amountEGP} showNote />
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {vehicle.features.map((feature: string, idx: number) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What the quote includes, and what changes it */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h2 className="text-2xl font-bold mb-6">{t("airportTransfers.luxor.includesTitle")}</h2>
                <ul className="space-y-3">
                  {includesItems.map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-6">{t("airportTransfers.luxor.changesTitle")}</h2>
                <ul className="space-y-3 mb-4">
                  {changesItems.map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">{t("airportTransfers.luxor.changesNote")}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Point-to-point. Sightseeing is a different product and pricing it as
            a transfer is how waiting time and itinerary cost get lost. */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="border-l-4 border-l-primary bg-background">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <Camera className="w-6 h-6 text-primary" />
                    {t("airportTransfers.luxor.p2pTitle")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{t("airportTransfers.luxor.p2pBody")}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">{t("airportTransfers.common.howItWorks")}</h2>
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {steps.map((step: any, index: number) => (
                  <div key={index} className="text-center">
                    <div className="step-number mb-4 mx-auto">{index + 1}</div>
                    <h3 className="font-semibold mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Details to send before booking */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">{t("airportTransfers.luxor.beforeTitle")}</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {beforeItems.map((item: string, index: number) => (
                  <li key={index} className="flex items-start gap-2 p-3 bg-background rounded-lg">
                    <CheckCircle className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Pricing Information */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">{t("airportTransfers.luxor.pricingTitle")}</h2>
              <p className="text-muted-foreground mb-8">
                {t("airportTransfers.luxor.pricingIntro")}
              </p>
              <div className="bg-muted/30 p-6 rounded-lg">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  <span className="font-semibold">{vehicleDigits("luxor-airport-transfer", "sedan") === null
                    ? "—"
                    : rawT("airportTransfers.common.startingFromRoutes", { price: vehicleDigits("luxor-airport-transfer", "sedan")! })}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  {t("airportTransfers.luxor.pricingTerms")}
                </p>
                <Button asChild size="lg">
                  <Link href="/transfers">
                    {t("airportTransfers.common.getInstantQuote")}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>

                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">{t("airportTransfers.luxor.ctaTitle")}</h2>
            <p className="text-lg mb-8 opacity-90">
              {t("airportTransfers.luxor.ctaSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-8">
                <Link href="/transfers">
                  {t("airportTransfers.luxor.ctaBook")}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>

              </Button>
              <a href="https://wa.me/201100765283" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white text-gray-900 border-gray-200 hover:bg-primary hover:text-white hover:border-primary">
                  <Phone className="w-5 h-5 mr-2" />
                  {t("airportTransfers.common.callNow")}
                </Button>
              </a>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    </>
  );
}